import {Box, Stack, Typography} from "@mui/material"
import React, {useState} from "react"
import {AppButton, AppSecondaryButton} from "@app-core/AppButton"
import {AnswerCard} from "components/AnswerCard"
import {SELECT_ANSWER_ERROR} from "data/constants/formMessages"
import {NAVY} from "theme"

interface SelectOneScreenProps {
  heading: string
  options: string[]
  value: string
  onSelect: (answer: string) => void
  onContinue: () => void
  onBack: () => void
  continueLabel?: string
  // Optional content rendered below the options (e.g. a conditional reassurance).
  children?: React.ReactNode
}

// Shared "heading + single-select radio cards + inline error + Back/Continue"
// screen. Each select-one screen becomes a heading + option list + routing.
export const SelectOneScreen: React.FC<SelectOneScreenProps> = (
  props: SelectOneScreenProps
) => {
  const {
    heading,
    options,
    value,
    onSelect,
    onContinue,
    onBack,
    continueLabel = "Continue",
    children
  } = props
  const [error, setError] = useState<string>("")

  const select = (answer: string) => {
    setError("")
    onSelect(answer)
  }

  // Continue is always active; show an inline error if nothing is selected.
  const handleContinue = () => {
    if (value === "") {
      setError(SELECT_ANSWER_ERROR)
      return
    }
    setError("")
    onContinue()
  }

  return (
    <Box padding={{xs: 3, sm: 6}}>
      <Typography
        component="h1"
        fontWeight="bold"
        fontSize={{xs: "24px", sm: "28px"}}
        color={NAVY}
        marginBottom={4}
      >
        {heading}
      </Typography>

      <Stack spacing={2}>
        {options.map((option) => (
          <AnswerCard
            key={option}
            label={option}
            selected={value === option}
            onClick={() => select(option)}
          />
        ))}
      </Stack>

      {children}

      {error !== "" && (
        <Box marginTop={3}>
          <Typography
            fontWeight="bold"
            fontSize="18px"
            component="div"
            role="alert"
          >
            {error}
          </Typography>
        </Box>
      )}

      <Box
        display="flex"
        alignItems="center"
        justifyContent="flex-end"
        gap={2}
        marginTop={4}
      >
        <AppSecondaryButton onClick={onBack} sx={{minWidth: "120px"}}>
          Back
        </AppSecondaryButton>
        <AppButton onClick={handleContinue} sx={{minWidth: "150px"}}>
          {continueLabel}
        </AppButton>
      </Box>
    </Box>
  )
}

export default SelectOneScreen
